import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import VFrequencyMark from '../components/VFrequencyMark';

interface Product {
  id: number;
  name: string;
  price: string;
  image: string;
}

// All images from public/images (ropa/, detalles de la ropa/, root)
const DETALLES = '/images/detalles%20de%20la%20ropa';
const products: Product[] = [
  { id: 1, name: 'Core Legging', price: '$88', image: '/images/ropa/VYERA_Legging_Carbon_Black_1.jpg' },
  { id: 2, name: 'Frequency Bra', price: '$62', image: `${DETALLES}/VYERA_CloseUp_SportsBra_Front.jpg` },
  { id: 3, name: 'Flow Legging', price: '$88', image: '/images/ropa/VYERA_Legging_Soft_Cream.jpg' },
  { id: 4, name: 'Daily Tee', price: '$48', image: '/images/VYERA_Oversized_Cream_Casual.png' },
  { id: 5, name: 'Restore Tank', price: '$42', image: '/images/ropa/VYERA_LightweightLayer_Warm_Grey.jpg' },
  { id: 6, name: 'Lightweight Layer', price: '$98', image: '/images/ropa/VYERA_LightweightLayer_Deep_Burgundy.jpg' },
  { id: 7, name: 'Lightweight Carbon', price: '$98', image: '/images/ropa/VYERA_LightweightLayer_Carbon_Black.png' },
  { id: 8, name: 'Legging Warm Grey', price: '$88', image: '/images/ropa/VYERA_Legging_Warm_Grey.jpg' },
  { id: 9, name: 'Oversized Black', price: '$52', image: '/images/ropa/VYERA_OversizedTee_Carbon_Black.png' },
  { id: 10, name: 'Core Legging II', price: '$88', image: '/images/ropa/VYERA_Legging_Carbon_Black_2.jpg' },
  { id: 11, name: 'Sports Bra Burgundy', price: '$62', image: '/images/ropa/VYERA_SportsBra_Deep_Burgundy.png' },
  { id: 12, name: 'Editorial', price: '—', image: '/images/VYERA_Editorial_3.png' },
];

export default function CollectionGrid() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const cards = section.querySelectorAll('.product-card');
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleCards((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="collection"
      className="relative bg-[var(--vyera-cream)] py-14 pb-12"
      style={{ zIndex: 60 }}
    >
      {/* V-Watermark Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.06]">
        <div className="absolute -right-[10vw] top-[10vh] w-[35vw] text-[var(--vyera-text-primary)]">
          <VFrequencyMark className="w-full h-auto" />
        </div>
        <div className="absolute -left-[5vw] bottom-[20vh] w-[25vw] text-[var(--vyera-text-primary)]">
          <VFrequencyMark className="w-full h-auto" />
        </div>
      </div>

      <div className="relative z-10 px-5 sm:px-[6vw]">
        {/* Heading Block */}
        <div className="mb-10 max-w-md">
          <h2 className="vyera-heading-lg text-[var(--vyera-text-primary)] mb-4">
            Shop the essentials
          </h2>
          <p className="vyera-body text-[var(--vyera-text-secondary)] mb-6">
            Pieces built for repetition. Designed to last.
          </p>
          <Link to="/shop" className="vyera-cta">
            View all
          </Link>
        </div>

        {/* Product Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-[3vw] gap-y-8"
        >
          {products.map((product, index) => (
            <div 
              key={product.id}
              data-index={index}
              className={`product-card group cursor-pointer transition-all duration-700 ${
                visibleCards.includes(index) 
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-8 scale-[0.98]'
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {/* Product Image */}
              <div className="vyera-img-card vyera-img-card--hover mb-4">
                <img
                  src={product.image}
                  alt={product.name}
                />
              </div>
              
              {/* Product Info */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-[15px] font-medium text-[var(--vyera-text-primary)] mb-1">
                    {product.name}
                  </h3>
                  <p className="text-[14px] text-[var(--vyera-text-secondary)]">
                    {product.price}
                  </p>
                </div>
                {product.price !== '—' ? (
                  <button className="px-4 py-2 bg-[var(--vyera-black)] text-[var(--vyera-cream)] text-[12px] font-medium hover:opacity-90 transition-opacity">
                    Add
                  </button>
                ) : (
                  <span className="vyera-cta text-[12px]">View</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
